import { z } from 'zod';
import { BusinessCodeSchema, RecordIdSchema } from './identifiers';
import { DecimalStringSchema } from './money';
import { RevenueRecognitionSchema } from './construction';
import { BusinessDateSchema, InstantSchema } from './time';

/**
 * Revenue recognition posted to the ledger (FIN-REVREC-001 … 003, owner-approved 2026-10-17; BD-71).
 *
 * Contracted value, collected cash and recognised revenue are three different things. Revenue is
 * recognised only under the rule the company **adopted** (`finance.revenueRecognition`), only after a
 * preview, and only when an authorized person posts it. What each contract may have recognised by a
 * date is computed from the source records (contract price without the maintenance deposit, signed
 * delivery, or price collected), less what was already recognised: a second run on the same date finds
 * nothing to do, a cancellation produces a derecognition, and posted history is never rewritten.
 */

export const RECOGNITION_DIRECTIONS = ['recognize', 'derecognize'] as const;
export const RecognitionDirectionSchema = z.enum(RECOGNITION_DIRECTIONS);
export type RecognitionDirection = z.infer<typeof RecognitionDirectionSchema>;

/** Why a contract is or is not recognisable under the rule, shown beside each candidate. */
export const RECOGNITION_BASES = [
  'contracted',
  'delivered',
  'collected',
  'notDelivered',
  'cancelled',
] as const;
export const RecognitionBasisSchema = z.enum(RECOGNITION_BASES);

export const RecognitionCandidateSchema = z.strictObject({
  contractId: RecordIdSchema,
  contractNumber: BusinessCodeSchema,
  projectId: RecordIdSchema,
  branchId: RecordIdSchema,
  basis: RecognitionBasisSchema,
  /** What the rule allows by `asOf`: price without the maintenance deposit, or the part collected. */
  recognizable: DecimalStringSchema,
  /** Already recognised, net of derecognitions. */
  recognized: DecimalStringSchema,
  /** recognizable − recognized; positive recognises, negative derecognises. */
  difference: DecimalStringSchema,
  direction: RecognitionDirectionSchema,
});
export type RecognitionCandidate = z.infer<typeof RecognitionCandidateSchema>;

export const RecognitionPreviewQuerySchema = z.strictObject({
  legalEntityId: RecordIdSchema,
  asOf: BusinessDateSchema,
});
export type RecognitionPreviewQuery = z.infer<typeof RecognitionPreviewQuerySchema>;

export const RecognitionPreviewSchema = z.strictObject({
  legalEntityId: RecordIdSchema,
  asOf: BusinessDateSchema,
  currency: z.string().regex(/^[A-Z]{3}$/),
  /** Null: no rule adopted, so nothing can be recognised and the preview lists nothing. */
  rule: RevenueRecognitionSchema.nullable(),
  ruleVersion: z.number().int().positive().optional(),
  candidates: z.array(RecognitionCandidateSchema),
  totals: z.strictObject({ recognize: DecimalStringSchema, derecognize: DecimalStringSchema }),
  /** Contracts already brought up to date by `asOf`, counted, not listed. */
  upToDate: z.number().int().nonnegative(),
  /** Whether a posting rule is active for each event, so the person knows if the ledger moves now. */
  postingRules: z.strictObject({ recognized: z.boolean(), derecognized: z.boolean() }),
});
export type RecognitionPreview = z.infer<typeof RecognitionPreviewSchema>;

export const PostRecognitionSchema = z.strictObject({
  legalEntityId: RecordIdSchema,
  asOf: BusinessDateSchema,
  /** The rule version the person previewed under; a change in between refuses the run. */
  expectedRuleVersion: z.number().int().positive(),
  /** A subset of the previewed contracts; all of them when omitted. */
  contractIds: z.array(RecordIdSchema).min(1).max(500).optional(),
  idempotencyKey: z.string().min(8).max(200),
});
export type PostRecognition = z.infer<typeof PostRecognitionSchema>;

export const RECOGNITION_STATES = ['posted', 'reversed'] as const;

export const RecognitionRecordSchema = z.strictObject({
  recognitionId: RecordIdSchema,
  batchId: RecordIdSchema,
  legalEntityId: RecordIdSchema,
  contractId: RecordIdSchema,
  contractNumber: BusinessCodeSchema,
  projectId: RecordIdSchema,
  asOf: BusinessDateSchema,
  rule: RevenueRecognitionSchema,
  ruleVersion: z.number().int().positive(),
  direction: RecognitionDirectionSchema,
  amount: DecimalStringSchema,
  currency: z.string().regex(/^[A-Z]{3}$/),
  state: z.enum(RECOGNITION_STATES),
  postedByAccountId: z.string().min(1).max(200),
  postedAt: InstantSchema,
  reversal: z
    .strictObject({
      reason: z.string(),
      byAccountId: z.string(),
      at: InstantSchema,
    })
    .optional(),
  version: z.number().int().positive(),
});
export type RecognitionRecord = z.infer<typeof RecognitionRecordSchema>;

export const PostRecognitionResultSchema = z.strictObject({
  batchId: RecordIdSchema,
  replayed: z.boolean(),
  records: z.array(RecognitionRecordSchema),
});

export const RecognitionQuerySchema = z.strictObject({
  legalEntityId: RecordIdSchema,
  contractId: RecordIdSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const ReverseRecognitionSchema = z.strictObject({
  reason: z.string().trim().min(5).max(500),
  expectedVersion: z.number().int().positive(),
});
export type ReverseRecognition = z.infer<typeof ReverseRecognitionSchema>;
